/**
 * Chamadas HTTP da agenda de médicos.
 *
 * Papel: aqui ficam as quatro chamadas de `/api/medicos` que a tela usa —
 * listar, criar, alterar e excluir um médico.
 *
 * Como usar: a listagem devolve o array inteiro, e não uma página. Nenhuma
 * transformação acontece aqui — o que sai é o que o schema validou, inclusive
 * o telefone já reduzido a dígitos.
 */
import {
  type ContatoMedico,
  type NovoContatoMedico,
  contatoMedicoSchema,
} from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

/** A listagem inteira, sem envelope de paginação. */
const listaDeMedicosSchema = z.array(contatoMedicoSchema);

/**
 * Lê todos os médicos, em ordem alfabética de nome.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns Os médicos cadastrados, possivelmente nenhum.
 */
export function buscarMedicos(sinal?: AbortSignal): Promise<ContatoMedico[]> {
  return clienteApi.get('/medicos', { schema: listaDeMedicosSchema, sinal });
}

/**
 * Cria um médico na agenda.
 *
 * @param dados - O médico preenchido, já validado por `novoContatoMedicoSchema`.
 * @returns O médico como ficou gravado.
 */
export function criarMedico(dados: NovoContatoMedico): Promise<ContatoMedico> {
  return clienteApi.post('/medicos', dados, { schema: contatoMedicoSchema });
}

/**
 * Substitui todos os campos de um médico.
 *
 * @param id - Identificador da linha em `contatos_medicos`.
 * @param dados - O médico inteiro, e não apenas os campos alterados.
 * @returns O médico como ficou gravado.
 */
export function atualizarMedico(id: string, dados: NovoContatoMedico): Promise<ContatoMedico> {
  return clienteApi.put(`/medicos/${encodeURIComponent(id)}`, dados, {
    schema: contatoMedicoSchema,
  });
}

/**
 * Apaga um médico da agenda.
 *
 * Não recebe schema porque a API responde 204 sem corpo.
 *
 * @param id - Identificador da linha em `contatos_medicos`.
 */
export function excluirMedico(id: string): Promise<void> {
  return clienteApi.remover(`/medicos/${encodeURIComponent(id)}`);
}
